import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useLiveData } from "@/lib/useLiveData";
import { Button } from "@/components/ui/button";
import { Car, FileText, ArrowRight, CheckCircle2 } from "lucide-react";
import { CAR_2012_URL, CAR_2010_URL, formatXCD } from "@/lib/constants";
import { usePortal, PORTAL_RENTALS } from "@/context/PortalContext";

export default function RentalsPortalPage() {
  const { setPortal } = usePortal();
  const [vehicles, setVehicles] = useState([]);
  const [content, setContent] = useState({});

  useLiveData(() => {
    setPortal(PORTAL_RENTALS);
    api.get("/vehicles").then(r => setVehicles(r.data)).catch(() => {});
    api.get("/content").then(r => setContent(r.data)).catch(() => {});
  });

  return (
    <Layout>
      <section className="bg-[#E32636] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Portal · Vehicle Rentals & Leasing</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-2xl">
            {content.rentals_tagline || "Affordable vehicle rentals in Antigua."}
          </h1>
          <p className="mt-4 max-w-xl text-white/90">Daily rentals, monthly leases, easy applications and online payments.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-2 gap-6">
        <Link to="/rentals" data-testid="portal-r-rent" className="group block bg-white border border-slate-200 rounded-md overflow-hidden hover:border-slate-400 hover:-translate-y-1 transition-all">
          <div className="aspect-[16/9] bg-slate-50 flex items-center justify-center p-8">
            <img src={CAR_2012_URL} alt="" className="max-h-full object-contain" />
          </div>
          <div className="p-6">
            <Car className="h-6 w-6 text-[#E32636]" strokeWidth={2.5} />
            <div className="font-heading font-black text-xl mt-2 text-slate-900">Vehicle Rentals</div>
            <p className="text-sm text-slate-600 mt-1">Pick a Honda Fit, choose dates, upload your license, drive.</p>
            <div className="mt-4 text-sm font-bold text-slate-900 inline-flex items-center gap-1">Browse vehicles <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" /></div>
          </div>
        </Link>
        <Link to="/leasing" data-testid="portal-r-lease" className="group block bg-white border border-slate-200 rounded-md overflow-hidden hover:border-slate-400 hover:-translate-y-1 transition-all">
          <div className="aspect-[16/9] bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center p-8">
            <FileText className="h-20 w-20 text-[#FFCE00]" strokeWidth={1.5} />
          </div>
          <div className="p-6">
            <FileText className="h-6 w-6 text-[#1D4ED8]" strokeWidth={2.5} />
            <div className="font-heading font-black text-xl mt-2 text-slate-900">Vehicle Leasing</div>
            <p className="text-sm text-slate-600 mt-1">Apply for a monthly lease — upload docs, track approval, pay online.</p>
            <div className="mt-4 text-sm font-bold text-slate-900 inline-flex items-center gap-1">Apply for lease <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" /></div>
          </div>
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="tag-overline">Available vehicles</div>
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-slate-900 mt-1">Pick your ride.</h2>
          </div>
          <Link to="/rentals" className="text-sm font-bold text-[#1D4ED8] hover:underline">View all →</Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {vehicles.slice(0, 4).map(v => (
            <Link key={v.id} to="/rentals" className="bg-white border border-slate-200 rounded-md overflow-hidden hover:border-slate-400 hover:-translate-y-1 transition-all">
              <div className="aspect-[16/10] bg-slate-50 flex items-center justify-center p-5">
                <img src={v.image_url} alt={v.model} className="max-h-full object-contain" />
              </div>
              <div className="p-5 border-t border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#1D4ED8]">{v.color}</div>
                  <div className="font-heading font-bold text-lg text-slate-900">{v.year} {v.make} {v.model}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading font-black text-2xl text-[#E32636]">{formatXCD(v.rate_per_day)}</div>
                  <div className="text-xs text-slate-500">per day</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}
